import { useCallback } from 'react';
import { useRequestTranscriptionsMutation } from '@frameio/graphql-client/generated';
import { intl } from '@frameio/i18n';
import { noop } from '@frameio/vapor';
import { useLocalOptimism } from '@frameio/vapor-migration/hooks';
import { useTrackUnsafe } from '../segment/hooks';
import { toSupportedLocale } from '../utils/toSupportedLocale';
import { useGeoFeature } from './useGeoFeature';
import { useUserPrefs } from './useUserPrefs';

export const useTranscriptionManager = ({
  assetIds,
  onError = noop,
  onSuccess = noop,
}: {
  assetIds?: string[];
  onError?: () => void;
  onSuccess?: (id: string) => void;
}) => {
  const track = useTrackUnsafe();
  const [
    requestTranscriptions,
    { error: mutationError, loading: mutationLoading, reset },
  ] = useRequestTranscriptionsMutation();

  const localeContext = intl();
  const locale = toSupportedLocale(localeContext.locale) || 'EN_US';

  const {
    userPrefs: { transcriptionsLanguage },
    setUserPrefs,
  } = useUserPrefs();
  const { isPermitted: canToggleSpeakerDiarization } = useGeoFeature({
    feature: 'canToggleSpeakerDiarization',
  });

  const [isDiarizationChecked, setIsDiarizationChecked] = useLocalOptimism(
    Boolean(canToggleSpeakerDiarization)
  );

  const setTranscriptionsLanguage = useCallback(
    (value: string) => setUserPrefs({ transcriptionsLanguage: value }),
    [setUserPrefs]
  );

  const transcribe = useCallback(() => {
    if (!assetIds || assetIds?.length < 1) return;

    track(
      'transcript-created',
      {
        asset_ids: assetIds,
        has_labeled_speakers: isDiarizationChecked,
        locale: transcriptionsLanguage,
      },
      { logToDatadog: true }
    );

    requestTranscriptions({
      variables: {
        input: {
          locale: transcriptionsLanguage,
          assetIds,
          diarization: isDiarizationChecked,
        },
        locale,
      },
      onError: () => {
        onError();
      },
      onCompleted: (response) => {
        onSuccess?.(
          response?.requestTranscriptions?.assets?.nodes?.[0]?.id || ''
        );
      },
    });
  }, [
    assetIds,
    track,
    isDiarizationChecked,
    transcriptionsLanguage,
    requestTranscriptions,
    locale,
    onError,
    onSuccess,
  ]);

  const handleCancel = useCallback(() => {
    reset();
  }, [reset]);

  const handleRetry = useCallback(() => {
    handleCancel();
    transcribe();
  }, [handleCancel, transcribe]);

  return {
    isDiarizationChecked,
    setIsDiarizationChecked,
    transcribe,
    transcribeError: mutationError,
    transcribeLoading: mutationLoading,
    transcriptionsLanguage,
    setTranscriptionsLanguage,
    handleRetry,
    handleCancel,
  };
};
